/**
 * Generic inbox detection — the single canonical copy.
 *
 * Firm-wide policy per Caleb: generic inbox addresses may never be stored on
 * contacts or outreach leads. Shared inboxes never reach a decision maker and
 * emailing them hurts sender reputation.
 *
 * This list lived in two places (server/outreach-import-core.ts and
 * client/src/lib/genericEmail.ts) with a "keep the list in sync" comment.
 * They had not drifted yet, but a rule enforced by two hand-synced copies
 * eventually drifts, and the client copy is what tells a user *why* their
 * input was rejected. Both now re-export from here.
 */

const GENERIC_LOCALS = new Set([
  "info", "contact", "contactus", "office", "admin", "administrator", "sales", "jobs", "careers",
  "hr", "estimating", "estimates", "estimator", "bids", "bid", "bidding", "reception", "frontdesk",
  "accounting", "billing", "invoices", "ap", "ar", "support", "help", "service", "services",
  "hello", "mail", "email", "marketing", "team", "dispatch", "shop", "general", "enquiries",
  "inquiries", "inquiry", "webmaster", "postmaster", "noreply", "no-reply", "donotreply",
]);

// Shown wherever a generic email is rejected — firm-wide policy per Caleb.
export const GENERIC_EMAIL_MESSAGE =
  "Generic inbox addresses (info@, sales@, office@, jobs@…) can't be stored on contacts. " +
  "Shared inboxes never reach a decision maker and hurt our sender reputation when emailed. " +
  "Save the person's direct email, or leave email blank and use Enrich (⚡) to find one.";

/**
 * Cold outreach to a shared inbox is wasted send volume: nobody named "info"
 * calls back about selling their business. Matches exact generic local parts
 * and prefixed forms like reception.companyname@gmail.com.
 */
export function isGenericInbox(email: string | null | undefined): boolean {
  const local = (email ?? "").split("@")[0].toLowerCase();
  if (!local) return false;
  if (GENERIC_LOCALS.has(local)) return true;
  const head = local.split(/[._-]/)[0];
  return head.length >= 2 && GENERIC_LOCALS.has(head) && local.length > head.length;
}
